<script setup lang="ts">
import { computed, nextTick, onMounted, onUnmounted, ref } from "vue";
import { RouterLink, useRoute, useRouter } from "vue-router";
import {
  ChevronDown,
  RefreshCw,
  LocateFixed,
  MapPin,
  Trash2,
  Plus,
  Pencil,
  Maximize,
  Minimize,
} from "@lucide/vue";
import { useUnitConversion } from "@/composables/useUnitConversion";
import SavedLocationShortcuts from "@/components/sidebar/SavedLocationShortcuts.vue";
import LocationWeatherBadge from "@/components/sidebar/LocationWeatherBadge.vue";
import type { LocationWeatherSummary } from "@/utils/locationSummary";
import type { TemperatureUnit } from "@/stores/settings.store";
import LocationSearch from "@/components/sidebar/LocationSearch.vue";
import { Button } from "@/components/ui/button";
import {
  MAX_LOCATION_NAME_LENGTH,
  type LocationHeaderData,
} from "@/stores/location.store";
import type {
  LocationSearchState,
  LocationSearchResult,
} from "@/stores/locationSearch.store";
import type { ROLocation } from "rainowls-shared";

const props = defineProps<{
  wallMode?: boolean;
  canFullscreen?: boolean;
  fullscreen?: boolean;
  locations: LocationHeaderData;
  searchState: LocationSearchState;
  refreshing: boolean;
  summaries: Map<string, LocationWeatherSummary>;
  summariesLoading: boolean;
  unit: TemperatureUnit;
}>();

const emit = defineEmits<{
  toggleFullscreen: [];
  refresh: [];
  select: [id: string | null];
  locate: [];
  save: [place: ROLocation];
  remove: [id: string];
  rename: [id: string, name: string];
  query: [value: string];
  chooseSearch: [place: LocationSearchResult];
  clearSearch: [];
}>();

const { labels } = useUnitConversion(() => props.unit);
const unitsDescription = computed(() =>
  props.unit === "Metric"
    ? "Celsius · Wind in kilometers per hour · Rain, snow and ice in millimeters · Pressure in millibars"
    : "Fahrenheit · Wind in miles per hour · Rain, snow and ice in inches · Pressure in millibars",
);

const route = useRoute();
const router = useRouter();
const open = ref(false);
const editingId = ref<string | null>(null);
const editedName = ref("");
const renameInput = ref<HTMLInputElement | null>(null);
const renameStatus = ref("");

const validName = computed(
  () =>
    editedName.value.trim().length > 0 &&
    editedName.value.trim().length <= MAX_LOCATION_NAME_LENGTH,
);

const now = ref(Date.now());
let clock: ReturnType<typeof setInterval> | undefined;
let menuOpener: HTMLButtonElement | null = null;
const container = ref<HTMLElement | null>(null);
const trigger = ref<HTMLButtonElement | null>(null);
const search = ref<InstanceType<typeof LocationSearch> | null>(null);
const selected = computed(() => props.locations.selected);

const savedCurrent = computed(() =>
  props.locations.savedLocations.find(
    (place) =>
      selected.value &&
      Math.abs(place.lat - selected.value.lat) < 0.0001 &&
      Math.abs(place.lng - selected.value.lng) < 0.0001,
  ),
);

const places = computed(() => props.locations.savedLocations);

/** Open the saved-location editor and focus its name field. */
async function startRename(id: string, name: string) {
  editingId.value = id;
  editedName.value = name;
  await nextTick();
  renameInput.value?.focus();
  renameInput.value?.select();
}

/** Close the editor and restore focus to the location’s edit button. */
async function cancelRename() {
  const id = editingId.value;
  editingId.value = null;
  await nextTick();
  const button = Array.from(
    container.value?.querySelectorAll<HTMLButtonElement>("[data-rename-id]") ??
      [],
  ).find((button) => button.dataset.renameId === id);
  button?.focus();
}

/** Save a valid location name and close the editor. */
function saveRename() {
  if (!editingId.value || !validName.value) return;
  emit("rename", editingId.value, editedName.value.trim());
  renameStatus.value = `Location renamed to ${editedName.value.trim()}.`;
  void cancelRename();
}

/** Focus location search when the menu opens. */
function focusMenu() {
  search.value?.focus();
}

/** Open the saved-location menu from the overflow button. */
async function openMore(button: HTMLButtonElement) {
  menuOpener = button;
  open.value = true;
  await nextTick();
  focusMenu();
}

/** Toggle the disclosure and manage its focus state. */
async function toggle() {
  menuOpener = trigger.value;
  editingId.value = null;
  open.value = !open.value;
  if (open.value) {
    await nextTick();
    focusMenu();
  }
}

/** Close the location menu and optionally restore focus. */
function close(restoreFocus = false) {
  open.value = false;
  editingId.value = null;
  emit("clearSearch");
  if (restoreFocus)
    (menuOpener?.isConnected ? menuOpener : trigger.value)?.focus();
}

/** Select a saved location and close the location menu. */
async function select(id: string | null) {
  emit("select", id);
  close(open.value);
  if (route.name !== "home" && route.name !== "map") await router.push("/");
}

/** Save the current location only when it is not already saved. */
function saveCurrent() {
  if (selected.value && !savedCurrent.value) emit("save", selected.value);
}

/** View a search result without automatically saving it. */
async function viewSearchResult(place: LocationSearchResult) {
  emit("chooseSearch", place);
  close();
  if (route.name !== "home" && route.name !== "map") await router.push("/");
}

/** Close the location menu when a pointer event occurs outside it. */
function outside(event: PointerEvent) {
  if (
    open.value &&
    container.value &&
    !container.value.contains(event.target as Node)
  )
    close();
}

/** Close the location menu and restore focus on Escape. */
function escape(event: KeyboardEvent) {
  if (event.key === "Escape" && open.value) close(true);
}

defineExpose({ container, trigger, search });

onMounted(() => {
  clock = setInterval(() => {
    now.value = Date.now();
  }, 60_000);
  document.addEventListener("pointerdown", outside);
  document.addEventListener("keydown", escape);
});

onUnmounted(() => {
  if (clock) clearInterval(clock);
  document.removeEventListener("pointerdown", outside);
  document.removeEventListener("keydown", escape);
});
</script>
<template>
  <header
    class="relative z-50 flex shrink-0 items-center gap-3 bg-app-bg"
    :class="[
      wallMode
        ? 'h-[var(--wall-header-height)] min-h-[var(--wall-header-height)] px-4 py-1.5'
        : 'min-h-16 px-3.5 py-2 md:min-h-[70px] md:px-6 md:py-3',
      { 'max-md:flex-wrap max-md:gap-2': route.name === 'ensembles' },
    ]"
  >
    <slot name="navigation" />
    <RouterLink
      to="/"
      class="shrink-0 focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent"
      :class="wallMode ? 'block' : 'flex md:hidden'"
      v-show="route.name !== 'ensembles' || wallMode"
      aria-label="RainOwls home"
      ><img
        :class="wallMode ? 'size-[34px]' : 'size-8'"
        src="@/assets/icons/AppIcon.svg"
        alt=""
    /></RouterLink>
    <h1
      v-if="route.name === 'ensembles'"
      class="shrink-0 text-[20px] font-[650] tracking-[-0.8px] md:text-[27px]"
    >
      Ensembles
    </h1>
    <div
      :class="{ 'max-md:flex-1': route.name === 'ensembles' }"
      ref="container"
      class="relative min-w-0 lg:max-w-[min(30%,360px)] lg:shrink-0"
    >
      <button
        ref="trigger"
        class="flex min-h-11 max-w-[min(520px,100%)] items-center justify-between gap-2.5 rounded-full border border-app-border bg-app-elevated px-1.5 text-left text-[16px] whitespace-nowrap text-text-primary transition-[transform,background-color,color] duration-[140ms] ease-[ease] hover:bg-app-selected focus-visible:outline-2 focus-visible:outline-offset-[3px] focus-visible:outline-accent active:translate-y-px aria-expanded:bg-app-selected motion-reduce:transform-none motion-reduce:transition-none md:px-3 md:text-[18px]"
        type="button"
        :aria-expanded="open"
        aria-haspopup="dialog"
        aria-label="Switch forecast location"
        @click="toggle"
      >
        <MapPin :size="17" class="shrink-0 text-navigation-active" /><span
          class="truncate"
          >{{ selected?.name || "Choose location" }}</span
        ><ChevronDown :size="15" class="shrink-0" />
      </button>
      <section
        v-if="open"
        id="header-location-menu"
        class="fixed inset-x-3 top-[68px] max-h-[calc(100dvh-160px)] w-auto overflow-auto rounded-panel border border-app-materialEdge bg-app-material p-4 shadow-popover md:absolute md:top-[calc(100%+12px)] md:right-auto md:left-0 md:max-h-none md:w-[390px] md:overflow-visible"
        role="dialog"
        aria-label="Locations"
      >
        <LocationSearch
          ref="search"
          :state="searchState"
          @query="emit('query', $event)"
          @select="viewSearchResult"
        />
        <p class="mx-3 my-2 text-[11px] text-text-muted">
          Search to view weather. Save a location only when you choose.
        </p>
        <Button
          variant="ghost"
          class="min-h-[42px] w-full justify-start gap-2.5 px-3 py-2 text-[13px] text-text-primary"
          :disabled="locations.isGeolocating"
          @click="emit('locate')"
        >
          <LocateFixed :size="17" />{{
            locations.isGeolocating
              ? "Finding your location…"
              : "Use my location"
          }}
        </Button>
        <p
          v-if="locations.geolocationError"
          role="status"
          class="mx-3 my-2 text-[11px] text-text-muted"
        >
          {{ locations.geolocationError }}
        </p>
        <div
          class="mx-3 mt-[18px] mb-2 flex justify-between text-xs leading-normal text-text-muted"
        >
          <h2 class="font-semibold">Saved locations</h2>
          <span
            >{{ props.locations.savedLocations.length }} /
            {{ locations.maxSavedLocations }}</span
          >
        </div>
        <p class="sr-only" role="status">{{ renameStatus }}</p>
        <div class="max-h-[45vh] overflow-auto">
          <div
            v-for="place in places"
            :key="place.id"
            class="place-row flex items-center rounded-control"
            :class="{
              selected:
                locations.selectedLocationId === place.id &&
                editingId !== place.id,
              'border border-app-border bg-app-material':
                editingId === place.id,
              'bg-app-selected':
                locations.selectedLocationId === place.id &&
                editingId !== place.id,
              'hover:bg-app-selected': editingId !== place.id,
            }"
          >
            <form
              v-if="editingId === place.id"
              class="w-full p-3"
              @submit.prevent="saveRename"
              @keydown.esc.stop.prevent="cancelRename"
            >
              <label
                class="mb-[7px] block text-[13px] font-semibold text-text-primary"
                :for="`rename-${place.id}`"
                >Saved location name</label
              >
              <input
                class="min-h-[42px] w-full min-w-0 rounded-small border border-app-border bg-app-bg px-3 py-2 text-base leading-normal text-text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-accent"
                :id="`rename-${place.id}`"
                :ref="
                  (element) =>
                    (renameInput = element as HTMLInputElement | null)
                "
                :aria-describedby="`rename-origin-${place.id}`"
                v-model="editedName"
                :maxlength="MAX_LOCATION_NAME_LENGTH"
                autocomplete="off"
                required
              />
              <p
                class="mt-3 text-xs leading-normal [overflow-wrap:anywhere] text-text-primary"
                :id="`rename-origin-${place.id}`"
              >
                <span class="block text-[11px] text-text-muted"
                  >Original location</span
                >
                {{
                  [place.city, place.country].filter(Boolean).join(", ") ||
                  `${place.lat.toFixed(4)}, ${place.lng.toFixed(4)}`
                }}
              </p>
              <div class="mt-3 grid grid-cols-2 items-center gap-2">
                <Button
                  size="small"
                  class="bg-app-elevated leading-normal text-text-primary"
                  @click="cancelRename"
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="small"
                  type="submit"
                  class="leading-normal disabled:opacity-40"
                  :disabled="!validName"
                >
                  Save name
                </Button>
              </div>
            </form>
            <template v-else>
              <Button
                variant="menu"
                class="min-w-0 flex-1 justify-start gap-3 border-0 p-3 text-left"
                :aria-pressed="locations.selectedLocationId === place.id"
                @click="select(place.id)"
              >
                <MapPin :size="17" class="shrink-0" /><span
                  class="min-w-0 flex-1"
                  ><strong
                    class="block truncate text-sm leading-normal font-[550]"
                    >{{ place.name }}</strong
                  ><small
                    class="mt-[3px] block truncate text-[11px] text-text-muted"
                    >{{ place.city || place.country || "Saved place" }}</small
                  ></span
                >
                <LocationWeatherBadge
                  :summary="summaries.get(place.id)"
                  :loading="summariesLoading"
                  :unit="unit"
                  :now="now"
                />
              </Button>
              <Button
                variant="ghost"
                size="icon-small"
                class="p-2.5"
                :data-rename-id="place.id"
                :aria-label="'Rename ' + place.name"
                :title="'Rename ' + place.name"
                @click="startRename(place.id, place.name)"
              >
                <Pencil :size="16" />
              </Button>
              <Button
                variant="ghost"
                size="icon-small"
                class="p-2.5"
                :aria-label="'Remove ' + place.name"
                @click="emit('remove', place.id)"
              >
                <Trash2 :size="16" />
              </Button>
            </template>
          </div>
          <div
            v-if="!places.length"
            class="grid justify-items-center gap-2 rounded-control bg-app-elevated px-4 py-6 text-center text-[13px] leading-normal text-text-muted"
          >
            <MapPin :size="24" aria-hidden="true" />
            <strong class="font-semibold text-text-primary"
              >No saved locations</strong
            >
            <p>
              {{
                selected
                  ? "Search above or save the current location."
                  : "Search above to view a location."
              }}
            </p>
          </div>
        </div>
        <Button
          v-if="selected && !savedCurrent"
          variant="primary"
          class="mt-3 min-h-[42px] w-full justify-start gap-2.5 px-3 py-2 text-[13px]"
          :disabled="!locations.canAddLocation"
          :title="
            !locations.canAddLocation
              ? `Limit of ${locations.maxSavedLocations} saved locations reached. Remove one to save this place.`
              : undefined
          "
          @click="saveCurrent"
        >
          <Plus :size="17" />Save current location
        </Button>
      </section>
    </div>

    <span
      v-if="places.length && !wallMode && route.name !== 'ensembles'"
      class="hidden h-6 w-px shrink-0 bg-app-border lg:block"
      aria-hidden="true"
    />
    <SavedLocationShortcuts
      v-if="places.length && !wallMode && route.name !== 'ensembles'"
      :places="places"
      :menu-open="open"
      :selected-id="locations.selectedLocationId"
      :summaries="summaries"
      :loading="summariesLoading"
      :unit="unit"
      :now="now"
      @select="select"
      @more="openMore"
    />
    <div
      v-if="route.name === 'ensembles'"
      id="ensemble-header-actions"
      class="ml-auto flex min-w-0 items-center justify-end gap-4 max-md:order-2 max-md:ml-0 max-md:basis-full max-md:justify-between"
    />
    <span
      class="inline-flex shrink-0 items-center gap-1 rounded-full border border-app-border bg-app-surface px-2.5 py-1 text-xs leading-4 text-text-muted whitespace-nowrap"
      :class="route.name === 'ensembles' ? 'ml-0' : 'ml-auto'"
      role="img"
      :aria-label="unitsDescription"
      :title="unitsDescription"
    >
      <span>{{ labels.temperature }}</span>
      <span class="hidden sm:inline" aria-hidden="true"
        >· {{ labels.speed }}</span
      >
      <span aria-hidden="true">·</span>
      <span>{{ labels.precipitation }}</span>
    </span>
    <Button
      variant="ghost"
      size="icon"
      class="shrink-0"
      aria-label="Refresh weather"
      title="Refresh weather"
      :disabled="refreshing"
      @click="emit('refresh')"
    >
      <RefreshCw
        :size="18"
        :class="{ 'animate-spin': refreshing }"
        aria-hidden="true"
      />
    </Button>
    <Button
      v-if="canFullscreen"
      variant="ghost"
      size="icon"
      class="shrink-0"
      :aria-label="fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
      :title="fullscreen ? 'Exit fullscreen' : 'Enter fullscreen'"
      :aria-pressed="fullscreen"
      @click="emit('toggleFullscreen')"
    >
      <component
        :is="fullscreen ? Minimize : Maximize"
        :size="20"
        aria-hidden="true"
      />
    </Button>
  </header>
</template>
